import React, { useEffect, useState } from "react";
import { Payment_History } from "../../hooks/Apis";
import {
  FaMoneyBillWave,
  FaUser,
  FaCalendarAlt,
  FaDownload,
  FaEnvelope,
  FaPhone,
  FaReceipt,
  FaFilter,
  FaSearch,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useAuth } from "../../hooks/AuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import CSDLogo_2 from "../../assets/CSDLogo.svg";

const PaymentHistory = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      try {
        // Build URL with phone parameter for non-admin users
        let url = Payment_History;
        if (user?.role !== "admin" && user?.phone_number) {
          url += `?phone=${user.phone_number}`;
        }

        const response = await fetch(url);
        const data = await response.json();

        console.log("Payment API Response:", data);

        if (data.error) {
          setError(data.error);
          setPayments([]);
        } else if (Array.isArray(data.payments)) {
          setPayments(data.payments);
        } else {
          setError("Unexpected response format");
          setPayments([]);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to fetch payment history");
        setPayments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentHistory();
  }, [user]); // Add user to dependency array

  const downloadInvoice = (payment) => {
    const doc = new jsPDF();
    const img = new Image();
    img.src = CSDLogo_2;

    img.onload = () => {
      // Header with logo
      doc.addImage(img, "PNG", 14, 10, 30, 30);
      doc.setFontSize(18);
      doc.setTextColor(245, 158, 11); // Yellow color
      doc.text("Cyber Space Digital", 50, 20);
      doc.setTextColor(0, 0, 0); // Black color
      doc.setFontSize(12);
      doc.text("INVOICE", 160, 20);

      const invoiceNumber = `CSD${String(payment.id).padStart(6, "0")}`;
      const invoiceDate = new Date(payment.created_at).toLocaleDateString();

      // Invoice details
      autoTable(doc, {
        startY: 45,
        head: [["Invoice Number", "Invoice Date"]],
        body: [[invoiceNumber, invoiceDate]],
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [245, 158, 11] }, // Yellow header
        theme: "grid",
      });

      // Customer details
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 5,
        head: [["Customer Details", "Contact Information"]],
        body: [
          [payment.name, `Phone: ${payment.phone}\nEmail: ${payment.email}`],
        ],
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [245, 158, 11] },
        theme: "grid",
      });

      // Payment items
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [["Description", "Quantity", "Unit Price", "Total"]],
        body: [
          [
            `Domain Renewal - ${payment.domain || "Service"}`,
            "1",
            `₹${payment.amount}`,
            `₹${payment.amount}`,
          ],
        ],
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [22, 160, 133] }, // Green header
      });

      // Summary
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 5,
        body: [
          ["Subtotal", `₹${payment.amount}`],
          ["Discount", "₹0.00"],
          ["Total Amount", `₹${payment.amount}`],
          ["Payment Status", payment.payment_status.toUpperCase()],
          ["Transaction ID", payment.txnid],
        ],
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: "bold" },
          1: { halign: "right" },
        },
        theme: "plain",
      });

      // Footer
      doc.setFontSize(9);
      const y = doc.internal.pageSize.height - 30;
      doc.setTextColor(245, 158, 11);
      doc.text("Cyber Space Digital | CSD", 14, y);
      doc.setTextColor(0, 0, 0);
      doc.text("Thank you for your business!", 14, y + 6);
      doc.text("This is a computer-generated invoice.", 14, y + 12);
      doc.text("Generated by CRMGENI.in", 14, y + 18);

      doc.save(`Invoice_${invoiceNumber}.pdf`);
    };
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "success":
        return {
          color: "green",
          icon: FaCheckCircle,
          badgeColor: "bg-green-100 text-green-800 border-green-200",
          text: "Successful",
        };
      case "created":
        return {
          color: "yellow",
          icon: FaClock,
          badgeColor: "bg-yellow-100 text-yellow-800 border-yellow-200",
          text: "Pending",
        };
      default:
        return {
          color: "red",
          icon: FaExclamationTriangle,
          badgeColor: "bg-red-100 text-red-800 border-red-200",
          text: "Failed",
        };
    }
  };

  // Filter payments based on user role and filters
  const userPayments = (
    user?.role === "admin"
      ? payments
      : payments.filter((p) => p.phone === user?.phone_number)
  ).filter((payment) => {
    // Status filter
    if (filter === "success" && payment.payment_status !== "success")
      return false;
    if (filter === "pending" && payment.payment_status !== "created")
      return false;
    if (filter === "failed" && payment.payment_status === "success")
      return false;

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        payment.name?.toLowerCase().includes(searchLower) ||
        payment.email?.toLowerCase().includes(searchLower) ||
        payment.phone?.includes(searchTerm) ||
        payment.txnid?.toLowerCase().includes(searchLower) ||
        payment.domain?.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading payment history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
          <FaExclamationTriangle className="text-4xl text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Something went wrong
          </h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-yellow-500 to-orange-600 bg-clip-text text-transparent mb-4">
            Payment History
          </h1>
          <p className="text-gray-600 text-lg">
            View and manage your transaction records
          </p>
        </div>

        {/* Stats Summary */}
        {userPayments.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-4 rounded-2xl shadow-lg border border-gray-100 text-center">
              <div className="text-2xl font-bold text-gray-800">
                {userPayments.length}
              </div>
              <div className="text-sm text-gray-600">Total Payments</div>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-lg border border-gray-100 text-center">
              <div className="text-2xl font-bold text-green-600">
                {
                  userPayments.filter((p) => p.payment_status === "success")
                    .length
                }
              </div>
              <div className="text-sm text-gray-600">Successful</div>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-lg border border-gray-100 text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {
                  userPayments.filter((p) => p.payment_status === "created")
                    .length
                }
              </div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-lg border border-gray-100 text-center">
              <div className="text-2xl font-bold text-red-600">
                {
                  userPayments.filter(
                    (p) =>
                      p.payment_status !== "success" &&
                      p.payment_status !== "created"
                  ).length
                }
              </div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            {/* Search Bar */}
            <div className="flex-1 w-full">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search payments by name, email, phone, or transaction ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filters */}
            <div className="flex flex-wrap gap-2">
              {[
                { key: "all", label: "All Payments", color: "gray" },
                { key: "success", label: "Successful", color: "green" },
                { key: "pending", label: "Pending", color: "yellow" },
                { key: "failed", label: "Failed", color: "red" },
              ].map(({ key, label, color }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                    filter === key
                      ? `bg-${color}-500 text-white shadow-lg`
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Payments List */}
        {userPayments.length > 0 ? (
          <div className="grid gap-6">
            {userPayments.map((payment) => {
              const status = getStatusInfo(payment.payment_status);
              const StatusIcon = status.icon;

              return (
                <div
                  key={payment.id}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  {/* Payment Header */}
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 border-b border-gray-200">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-white rounded-xl shadow-sm">
                          <FaReceipt className="text-2xl text-yellow-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">
                            Transaction: {payment.txnid}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <div
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${status.badgeColor}`}
                            >
                              <StatusIcon className="text-sm" />
                              <span className="text-sm font-medium">
                                {status.text}
                              </span>
                            </div>
                            <span className="text-sm text-gray-600">
                              {new Date(
                                payment.created_at
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="bg-white px-3 py-2 rounded-full border">
                          <span className="text-lg font-bold text-yellow-700">
                            ₹{payment.amount}
                          </span>
                        </div>
                        <button
                          onClick={() => downloadInvoice(payment)}
                          className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold px-4 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
                        >
                          <FaDownload className="text-sm" />
                          Invoice
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Payment Details */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
                      <div className="flex items-center gap-3">
                        <FaUser className="text-yellow-500 text-xl" />
                        <div>
                          <p className="text-sm text-gray-500">Customer</p>
                          <p className="font-semibold text-gray-800">
                            {payment.name}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <FaEnvelope className="text-blue-500 text-xl" />
                        <div>
                          <p className="text-sm text-gray-500">Email</p>
                          <p className="font-semibold text-gray-800">
                            {payment.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <FaPhone className="text-green-500 text-xl" />
                        <div>
                          <p className="text-sm text-gray-500">Phone</p>
                          <p className="font-semibold text-gray-800">
                            {payment.phone}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <FaCalendarAlt className="text-purple-500 text-xl" />
                        <div>
                          <p className="text-sm text-gray-500">Date</p>
                          <p className="font-semibold text-gray-800">
                            {new Date(payment.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Domain and Additional Info */}
                    {payment.domain && (
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <div className="flex items-center gap-2">
                          <FaMoneyBillWave className="text-yellow-500" />
                          <span className="text-sm font-medium text-gray-700">
                            Domain:
                          </span>
                          <span className="text-sm text-gray-800">
                            {payment.domain}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-100 max-w-md mx-auto">
              <FaReceipt className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {payments.length === 0
                  ? "No payments found"
                  : "No matching payments"}
              </h3>
              <p className="text-gray-600 mb-6">
                {payments.length === 0
                  ? "You don't have any payment records yet."
                  : `No payments match your current search or filter.`}
              </p>
              {payments.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-sm text-yellow-800">
                    Your payment history will appear here after completing
                    transactions.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
